import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApplications, useAsync, useJourney } from '../hooks/usePortal';
import { portal, type RejectionEnvelope } from '../lib/portal';
import { normaliseSteps, needsAttention, needsReviewAttention, progress, deepLinkStateFor } from '../lib/steps';
import { track } from '../lib/analytics';
import ProgramJourney from '../components/ProgramJourney';
import RenewalEligibilityCard from '../components/RenewalEligibilityCard';
import { ArrivalInformationCard } from '../components/ArrivalInformationCard';
import { LocalTravelOptionsCard } from '../components/LocalTravelOptionsCard';
import { usePortalContext } from '../hooks/usePortalContext';

/**
 * Dashboard: live program journey from Globus step-progress, structured
 * rejection-envelope messaging (AP-1, codes + messages rendered verbatim,
 * reasonCode emitted to analytics), and a real start-application flow.
 */
export default function DashboardPage() {
  const navigate = useNavigate();
  const { ctx, programConfig } = usePortalContext();
  const { apps, active, error, loading, reload } = useApplications();
  const stepsState = useJourney(active?.id);
  const steps = normaliseSteps(stepsState.data);
  const { done, total, pct } = progress(steps);

  // Warm the form page while the applicant is still reading the dashboard:
  // the heavy steps payload goes into the shared query cache and the ~900KB
  // engine chunk gets downloaded+parsed, so clicking into a step renders the
  // form immediately instead of skeleton → fetch → chunk-parse. Idle-deferred
  // so it never competes with the dashboard's own requests.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!active?.id) return;
    const idle = (cb: () => void) =>
      'requestIdleCallback' in window ? requestIdleCallback(() => cb(), { timeout: 4000 }) : setTimeout(cb, 1500);
    const handle = idle(() => {
      void queryClient.prefetchQuery({ queryKey: ['steps', String(active.id)], queryFn: () => portal.steps(active.id) });
      void import('@alliance-strategies/form-engine-portable');
    });
    return () => ('cancelIdleCallback' in window ? cancelIdleCallback(handle as number) : clearTimeout(handle as number));
  }, [active?.id, queryClient]);

  // Rejection envelope (application.created / status): render codes verbatim.
  // `metadata.rejection` is the producer's home; a bare top-level `rejection`
  // key is the legacy spelling still seen on older application rows.
  const envelope = (active?.metadata?.rejection ??
    (active as { rejection?: unknown } | null | undefined)?.rejection) as RejectionEnvelope | undefined;
  // Dashboard-alert impression: the alert card below renders for ANY truthy
  // envelope (reason-code-only, message-only, even empty {}), so the impression
  // fires whenever the card renders — not only when reasonCode is set — with
  // the same reasonCode-or-legacy-code the card's pill displays (null when the
  // envelope carries neither). Fired once per application per mount; gated on
  // the page actually rendering the card (not the loading/error early returns).
  const alertFiredForApp = useRef<string | number | undefined>(undefined);
  useEffect(() => {
    if (loading || error) return; // page renders the loading/error card, not the alert
    if (!active?.id || !envelope || alertFiredForApp.current === active.id) return;
    alertFiredForApp.current = active.id;
    track('rejection_envelope_shown', {
      surface: 'dashboard_alert',
      applicationId: active.id,
      reasonCode: envelope.reasonCode ?? envelope.code ?? null,
    });
  }, [active?.id, envelope, loading, error]);

  // Journey-surface impression (Asana 1216270468356070): ProgramJourney stays
  // presentation-pure, so its needs-attention badge impression is fired here,
  // where the journey is mounted — one aggregate event per application load
  // (NOT per node; JourneyNode re-renders constantly). reasonCode is null by
  // design — see ApplicationPage's rejection-surface impression comment for why.
  const journeyFiredForApp = useRef<string | number | undefined>(undefined);
  useEffect(() => {
    if (loading || error) return; // page renders the loading/error card, not the journey
    if (!active?.id || journeyFiredForApp.current === active.id) return;
    // Rejection impressions only — compliance feedback is not a staff rejection.
    const attention = steps.filter(needsReviewAttention);
    if (attention.length === 0) return;
    journeyFiredForApp.current = active.id;
    track('rejection_envelope_shown', {
      surface: 'journey',
      applicationId: active.id,
      stepKeys: attention.map((s) => s.stepKey),
      count: attention.length,
      reasonCode: null,
    });
  }, [active?.id, steps, loading, error]);

  if (loading) return <div className="card">Loading your journey…</div>;
  if (error) return <ErrorCard message={error} onRetry={reload} />;
  if (!active) return <StartApplication onCreated={reload} />;

  return (
    <>
      <div className="hello">
        <h1>Welcome back{active.firstName ? `, ${active.firstName}` : ''} 👋</h1>
        <p>
          {/* Program + brand from the RESOLVED context (Globus program config /
              brand catalogue), falling back to the application row's own
              enrichment — same live source, never a hardcoded name. */}
          {programConfig?.config?.name ?? ctx?.program?.name ?? active.programName ?? 'Your program'}
          {(ctx?.brand?.name ?? active.brandName) ? ` · ${ctx?.brand?.name ?? active.brandName}` : ''}
          {ctx?.season?.name ? ` · ${ctx.season.name}` : ''} — {done} of {total} steps complete.
        </p>
      </div>

      {envelope && (
        <div className="card alertcard" role="alert">
          <h2>Application update <span className="pill err">{envelope.reasonCode ?? envelope.code ?? 'REJECTED'}</span></h2>
          <p>{envelope.message ?? envelope.reason ?? 'Your application needs attention.'}</p>
        </div>
      )}

      <div className="card">
        <h2>
          Your program journey{' '}
          <span className={pct === 100 ? 'pill ok' : 'pill warn'}>{pct === 100 ? 'Complete' : `${pct}%`}</span>
        </h2>
        <ProgramJourney
          steps={steps}
          complete={pct === 100}
          onStepClick={(s) => navigate('/application', { state: deepLinkStateFor(s) })}
        />
        {steps.some((s) => s.globusMessage) && (
          <ul className="feed">
            {steps.filter((s) => s.globusMessage).map((s) => (
              <li key={s.stepKey}>
                <strong>{s.title}:</strong>{' '}
                {needsAttention(s) && <span className="pill err">Action needed</span>}{' '}
                <span className="globus-msg">{s.globusMessage}</span>
              </li>
            ))}
          </ul>
        )}
        {steps.length === 0 && <p>Your workflow is being prepared — check back shortly.</p>}
      </div>

      <div className="cardgrid">
        {/* Arrival information (Task #1673): renders only when Globus confirms
            a placement (available: true). Silently absent pre-placement. */}
        <ArrivalInformationCard />

        {/* Renewal/extension eligibility + create (renders only when Globus
            reports an eligible flow — see RenewalEligibilityCard).
            couldBeEligible gates the availability fetch on data this page
            already holds: Globus only ever grants renewal/extension to a
            finished (or terminal-stage) application, so anyone mid-journey —
            pct < 100 with a non-terminal stage AND a non-terminal status (the
            same terminal set useApplications filters on) — can never be
            eligible and skips the Hub round-trip entirely.

            `active` is the newest NON-terminal application, and it is also the
            id the create uses as the prior application. Open product question
            F15 (documented in full on ApplicationsController.createFlow):
            renewal is most plausible once the program has COMPLETED, i.e. is
            terminal, and for an all-terminal applicant `active` falls back to
            apps[0] — so with several terminal applications this keys off an
            arbitrary one. Not resolved portal-side on purpose; Globus owes the
            rule.

            NO onCreated handler on purpose: the card invalidates
            ['applications'] itself, and that is the very query useApplications
            observes here — so this page re-resolves `active` onto the new
            application without any help. Passing reload() made every create
            fetch the list twice. */}
        <RenewalEligibilityCard
          applicationId={active.id}
          programName={programConfig?.config?.name ?? ctx?.program?.name ?? active.programName}
          couldBeEligible={
            pct === 100 ||
            active.stageIsTerminal === true ||
            ['completed', 'withdrawn', 'rejected'].includes((active.status ?? '').toLowerCase())
          }
        />
        {/* Local Travel Options: placement-gated (the former static tile was
            the real day-1 travel surface — see LocalTravelOptionsCard).
            Shares the arrival-information query with the card above. */}
        <LocalTravelOptionsCard />
      </div>
    </>
  );
}

function ErrorCard({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="card" role="alert">
      <h2>Couldn't load your data</h2>
      <p>{message}</p>
      <p style={{ marginTop: 12 }}><button onClick={onRetry}>Try again</button></p>
    </div>
  );
}

/** No application yet → real create flow against live programs (AP-1 event contract). */
function StartApplication({ onCreated }: { onCreated: () => void }) {
  const { data: programs, error, loading } = useAsync(() => portal.programs(), []);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();

  const start = async (programId: string) => {
    setBusy(true);
    setFailure(undefined);
    try {
      await portal.createApplication(programId);
      track('application_created', { programId });
      onCreated();
    } catch (e) {
      setFailure((e as Error).message);
      track('application_create_rejected', { programId, error: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="card">Loading programs…</div>;
  return (
    <div className="card">
      <h1>Start your journey</h1>
      <p>Choose a program to begin your application.</p>
      {error && <p role="alert">{error}</p>}
      {failure && <p role="alert">{failure}</p>}
      <ul className="feed" style={{ marginTop: 14 }}>
        {(programs ?? []).map((p) => (
          <li key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <span><strong>{p.name ?? p.programName}</strong>{p.brandName ? ` · ${p.brandName}` : ''}</span>
            <button disabled={busy} onClick={() => start(String(p.id))} style={{ width: 'auto' }}>Apply</button>
          </li>
        ))}
        {(programs ?? []).length === 0 && <p>No programs are open for applications right now.</p>}
      </ul>
    </div>
  );
}
